import { Injectable } from '@angular/core';
import { Logger } from '@app/core/services/logger.service';
import { Package } from '@core/models/package.model';
import { PlacementCategory } from '@core/models/placement-category.model';
import { Placement } from '@core/models/placement.model';
import { Product, Publisher } from '@core/models/product.model';
import { PackageService } from '@core/services/package.service';
import { PlacementCategoryService } from '@core/services/placement-category.service';
import { combineLatest, map, Observable, of, pipe, shareReplay } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class StaticDataCacheService {

	private _isReady = false;

	private _placements?: Map<string, Placement>;
	private _products?: Map<string, Product>;
	private _packages?: Map<string, Package>; // package id to package
	private _publishers?: Publisher[];
	private _publishers$?: Observable<Publisher[]>;

	constructor (
		private _packageService: PackageService,
		private _placementCategoryService: PlacementCategoryService
	) { }

	init(): Observable<void> {
		if (this._isReady) {
			return of(void (0)); // resolve immediately
		}

		const placementCategories$: Observable<PlacementCategory[]> = this._placementCategoryService.getPlacementCategories();
		const packages$: Observable<Package[]> = this._packageService.getAllPackages();

		return combineLatest([placementCategories$, packages$]).pipe(
			map(([placementCategories, packages]) => {
				this.initPlacements(placementCategories);
				this.initPackages(packages);
				this._isReady = true;
			})
		);
	}

	get packages(): Package[] {
		if (!this._isReady) {
			throw "The StaticDataCacheService is not initialized. Please call init() and subscribe for its Observable";
		}

		return Array.from(this._packages!.values());
	}

	get products(): Product[] {
		if (!this._isReady) {
			throw "The StaticDataCacheService is not initialized. Please call init() and subscribe for its Observable";
		}

		return Array.from(this._products!.values());
	}

	get placements(): Placement[] {
		if (!this._isReady) {
			throw "The StaticDataCacheService is not initialized. Please call init() and subscribe for its Observable";
		}

		return Array.from(this._placements!.values());
	}

	get publishers(): Publisher[] {
		if (!this._isReady) {
			throw "The StaticDataCacheService is not initialized. Please call init() and subscribe for its Observable";
		}

		return this._publishers!;
	}

	// I made this function self-init (and therefore an Observable) so that I don't have to init the whole cache when just wanting the publishers.
	// Maybe we should remove the need to init everything and instead allow loading on demand packages and placements separately as observables.
	get publishers$(): Observable<Publisher[]> {
		if (!this._publishers$) {
			this._publishers$ = this._packageService.getAllPackages().pipe(
				map(packages => {
					this.initPackages(packages);
					return this._publishers!;
				}),
				shareReplay()
			);
		}
		return this._publishers$;
	}

	getPackageById(packageId: string): Package | undefined {
		if (!this._isReady) {
			throw "The StaticDataCacheService is not initialized. Please call init() and subscribe for its Observable";
		}

		return this._packages!.get(packageId);
	}

	getProductById(productId: string): Product | undefined {
		if (!this._isReady) {
			throw "The StaticDataCacheService is not initialized. Please call init() and subscribe for its Observable";
		}

		return this._products!.get(productId);
	}

	getPlacement(packageId: string, placementId: string): Placement | undefined {

		// the package Id will be used later

		if (!this._isReady) {
			throw "The StaticDataCacheService is not initialized. Please call init() and subscribe for its Observable";
		}

		return this._placements!.get(placementId);
	}

	private initPlacements(placementCategories: PlacementCategory[] | undefined): void {
		// For now, to get the placements list, use placementCategories but take the first label for each placement code.
		// Later, we will change to use a new API endpoint that only has a distinct list of codes(with their labels).
		if (!this._placements) {
			this._placements = new Map<string, Placement>;
			if (placementCategories && placementCategories.length > 0) {
				for (let placementCategory of placementCategories) {

					if (placementCategory.placements && placementCategory.placements.length > 0) {
						for (let placement of placementCategory.placements) {

							if (!this._placements.has(placement.id)) {
								this._placements.set(placement.id, placement);
							}
						}
					}
				}
			}
		}
	}

	private initPackages(packages: Package[] | undefined): void {
		if (!this._packages) {
			this._packages = new Map<string, Package>;
			this._products = new Map<string, Product>;
			this._publishers = [];
			if (packages && packages.length > 0) {
				for (let pckg of packages) {
					if (pckg.label) {
						this._packages.set(pckg.id, pckg);
					}
					if (pckg.products && pckg.products.length > 0) {
						for (let product of pckg.products) {
							this._products.set(product.id, product);
							const comp = product.company;
							if (comp) {
								if (!this._publishers.some(x => x.id === comp.id)) {
									this._publishers.push(comp);
								}
							}
						}
					}
				}
			}
		}
	}
}

